import {
  EmailAccountKinds,
  NewEmailAccount,
} from '@mm/state/interfaces/emailAccount';
import { landmineNames } from '@mm/constants/landmine-names';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { assertUnreachable } from '@mm/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useChannelConnection } from '../../../editor/features/beta/modules/Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { useFreeTrialUpgradeModal } from '../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

export const useActivateEmailAccount = (
  setMicrosoftPopup?: (popup: Window | null) => void,
) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const { nativeIntegrations: hasNativeIntegrationsFeature } = useSelector(
    combinedFeatureFlagsSelector,
  );

  const { connectChannel } = useChannelConnection(() => {});
  const { getAccounts, getMicrosoftOauthUrl } = useEmailAccountCRUDActions();
  const { openConnectSendGridModal, openEmailAccountEditorModal, closeModal } =
    useModalActions();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();

  const [microsoftAccountPopup, setMicrosoftAccountPopup] =
    useState<Window | null>(null);

  useEffect(() => {
    if (!microsoftAccountPopup) {
      return;
    }
    const onMessage = event => {
      if (event.origin !== new URL(API_URL).origin) {
        return;
      }
      const { data } = event;
      if (data?.type === 'microsoftAccountConnected') {
        microsoftAccountPopup.close();
        setMicrosoftAccountPopup(null);
        setMicrosoftPopup?.(null);
        getAccounts(facebookPageId);
        closeModal();
      }
    };
    window.addEventListener('message', onMessage);
    const checkIfPopupClosedInterval = setInterval(() => {
      if (microsoftAccountPopup.closed) {
        setMicrosoftAccountPopup(null);
        setMicrosoftPopup?.(null);
        getAccounts(facebookPageId);
      }
    }, 1000);
    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(checkIfPopupClosedInterval);
    };
  }, [microsoftAccountPopup]);

  const connectMicrosoftAccount = useCallback(async () => {
    const popup = window.open(
      await getMicrosoftOauthUrl(facebookPageId),
      'microsoftConnect',
      'popup,width=600,height=600',
    );
    setMicrosoftAccountPopup(popup);
    setMicrosoftPopup?.(popup);
  }, [facebookPageId, getMicrosoftOauthUrl, setMicrosoftPopup]);

  const createNewManualAccount = useCallback(() => {
    if (!hasNativeIntegrationsFeature) {
      openFreeTrialUpgradeModalPromotional({
        landmineName: landmineNames.ACTIVATE_EMAIL_ACCOUNT_MANUAL,
      });
      return;
    }
    const defaultCustomEmailAccountConfig = {
      name: '',
      emailAddress: '',
      kind: 'custom',
      credential: {
        smtpServerParams: {
          address: '',
          port: 465,
          userName: '',
          password: '',
          enableSsl: true,
        },
        incomingServerType: 'imap',
        incomingServerParams: {
          address: '',
          port: 993,
          userName: '',
          password: '',
          enableSsl: true,
        },
      },
    } as NewEmailAccount;

    openEmailAccountEditorModal({
      emailAccountUnderEdit: defaultCustomEmailAccountConfig,
    });
  }, [
    openEmailAccountEditorModal,
    hasNativeIntegrationsFeature,
    openFreeTrialUpgradeModalPromotional,
  ]);

  const connectEmailAccount = useMemo(
    () => (kind: EmailAccountKinds) => {
      switch (kind) {
        case 'gmail':
          connectChannel('email');
          break;
        case 'microsoft':
          connectMicrosoftAccount();
          break;
        case 'sendgrid':
          openConnectSendGridModal();
          break;
        case 'custom':
          createNewManualAccount();
          break;
        default:
          assertUnreachable(kind);
      }
    },
    [
      openConnectSendGridModal,
      connectChannel,
      createNewManualAccount,
      connectMicrosoftAccount,
    ],
  );

  return {
    connectEmailAccount,
  };
};
